import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllCategories } from '../services/categoryService.js';
import {
    getAllClassrooms,
    addClassroom,
    updateClassroom,
    deleteClassroom,
    ensureClassroomInvite,
    rotateClassroomInviteToken,
    ALL_STUDENTS_CLASSROOM_ID,
    // assignTeacherToClassroom, // Import if needed for UI
    // removeTeacherFromClassroom // Import if needed for UI
} from '../services/classroomService.js';
import { useAuth } from '../components/AuthProvider.js'; // Import useAuth
import '../styles/ManageClassroomsPage.css';

function ManageClassroomsPage() {
  const { currentUser } = useAuth(); // Get current logged-in user
  const [classrooms, setClassrooms] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  // Initialize with teacherIds as empty array
  const [currentClassroom, setCurrentClassroom] = useState({ id: null, name: '', description: '', categoryIds: [], teacherIds: [] });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteActions, setInviteActions] = useState({});

  const aggregateClassroomTemplate = {
    id: ALL_STUDENTS_CLASSROOM_ID,
    name: 'Todos os alunos',
    description: 'Acompanhe o desempenho de todos os alunos cadastrados, inclusive os sem turma.',
    teacherIds: [],
    categoryIds: [],
    isAggregateView: true,
    inviteUrl: null,
    inviteToken: null,
    inviteExpiresAt: null,
    creatorId: null
  };

  const isVirtualClassroom = (classroom) => classroom?.isAggregateView || classroom?.id === ALL_STUDENTS_CLASSROOM_ID;

  // Fetch classrooms managed by the current teacher and all categories
  useEffect(() => {
    if (currentUser && (currentUser.role === 'teacher' || currentUser.role === 'admin')) {
      fetchManagedClassrooms(); // Fetch only teacher's classrooms
      fetchCategories();
    } else {
        setError("You must be logged in as a teacher to manage classrooms.");
        // Or redirect, depending on App.js protection level
    }
  }, [currentUser]); // Re-run if currentUser changes

  const fetchManagedClassrooms = async () => {
    if (!currentUser?.uid) return; // Ensure currentUser and uid exist

    setIsLoading(true);
    setError('');
    try {
      const teacherFilter = currentUser.role === 'admin' ? null : currentUser.uid;
      const fetchedClassrooms = await getAllClassrooms(teacherFilter);
      const withAggregate = currentUser.role === 'admin'
        ? [
            { ...aggregateClassroomTemplate },
            ...fetchedClassrooms
          ]
        : fetchedClassrooms;
      setClassrooms(withAggregate);
      setInviteActions({});
    } catch (err) {
      setError('Failed to fetch your classrooms. Please try again.');
      console.error("Fetch Managed Classrooms Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
     try {
      const fetchedCategories = await getAllCategories();
      setAllCategories(fetchedCategories);
    } catch (err) {
      setError(prev => prev + (prev ? ' ' : '') + 'Failed to fetch categories.');
      console.error("Fetch Categories Error:", err);
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentClassroom({ ...currentClassroom, [name]: value });
  };

  const handleCategoryChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setCurrentClassroom({ ...currentClassroom, categoryIds: selectedOptions });
  };

  // --- TODO: Add UI and handler for managing teacherIds ---
  // Example: handleTeacherChange (using a multi-select or checkboxes)
  // const handleTeacherChange = (e) => {
  //   const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
  //   setCurrentClassroom({ ...currentClassroom, teacherIds: selectedOptions });
  // };
  // ---

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser?.uid) {
        setError("Authentication error. Please log in again.");
        return;
    }
    setIsLoading(true);
    setError('');

    // Prepare data, ensuring teacherIds is an array
    const classroomData = {
        name: currentClassroom.name,
        description: currentClassroom.description,
        categoryIds: currentClassroom.categoryIds || [],
        teacherIds: currentClassroom.teacherIds || [], // Ensure it's an array
        isDefault: currentClassroom.isDefault || false // Handle isDefault checkbox if added
    };

    try {
      if (isEditing) {
        // --- Permission Check for Editing ---
        const classroomToEdit = classrooms.find(c => c.id === currentClassroom.id);
        if (!classroomToEdit || !classroomToEdit.teacherIds.includes(currentUser.uid)) {
             setError("You do not have permission to edit this classroom.");
             setIsLoading(false);
             return;
        }
        // Ensure creator cannot be removed from teacherIds accidentally if UI is added
        if (classroomToEdit.creatorId && !classroomData.teacherIds.includes(classroomToEdit.creatorId)) {
            classroomData.teacherIds.push(classroomToEdit.creatorId); // Ensure creator stays
        }
        // --- End Permission Check ---

        await updateClassroom(currentClassroom.id, classroomData);
      } else {
        // When adding, creatorId is the current user, teacherIds starts with creator
        // The service now handles adding the creatorId and initializing teacherIds
        await addClassroom(classroomData, currentUser.uid);
      }
      resetForm();
      await fetchManagedClassrooms(); // Refresh list
    } catch (err) {
      setError(isEditing ? 'Failed to update classroom. Please try again.' : 'Failed to add classroom. Please try again.');
      console.error("Submit Classroom Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (classroom) => {
    if (isVirtualClassroom(classroom)) {
      return;
    }

    // --- Permission Check for Editing ---
     if (!currentUser || !classroom.teacherIds.includes(currentUser.uid)) {
         setError("You do not have permission to edit this classroom.");
         return;
     }
    // --- End Permission Check ---

    setIsEditing(true);
    setCurrentClassroom({
        id: classroom.id,
        name: classroom.name,
        description: classroom.description,
        categoryIds: classroom.categoryIds || [],
        teacherIds: classroom.teacherIds || [], // Load existing teachers
        isDefault: classroom.isDefault || false // Load isDefault status
    });
  };

  const handleDelete = async (classroom) => {
    if (isVirtualClassroom(classroom)) {
      return;
    }

    // --- Permission Check for Deleting (Only Creator) ---
     if (!currentUser || classroom.creatorId !== currentUser.uid) {
         setError("Only the creator can delete this classroom.");
         return;
     }
    // --- End Permission Check ---

    if (window.confirm(`Are you sure you want to delete classroom "${classroom.name}"? This will unlink associated students.`)) {
      setIsLoading(true);
      setError('');
      try {
        // Pass creatorId to service for potential cleanup in teacher's record
        await deleteClassroom(classroom.id, classroom.creatorId);
        await fetchManagedClassrooms(); // Refresh list
        resetForm(); // Reset form if the deleted item was being edited
      } catch (err) {
        setError('Failed to delete classroom. Check console for details.');
        console.error("Delete Classroom Error:", err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resetForm = () => {
    setIsEditing(false);
    setCurrentClassroom({ id: null, name: '', description: '', categoryIds: [], teacherIds: [] });
  };

  const copyToClipboard = async (text) => {
    if (!text) {
      return false;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (clipboardError) {
      console.warn('Navigator clipboard write failed, attempting fallback.', clipboardError);
    }

    if (typeof document === 'undefined') {
      return false;
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      return successful;
    } catch (fallbackError) {
      console.error('Fallback clipboard copy failed.', fallbackError);
      return false;
    }
  };

  const updateInviteState = (classroomId, updater) => {
    setInviteActions((prev) => {
      const previous = prev[classroomId] || {};
      const next = typeof updater === 'function' ? updater(previous) : updater;
      return {
        ...prev,
        [classroomId]: {
          copySuccess: false,
          error: '',
          rotating: false,
          ensuring: false,
          ...previous,
          ...next
        }
      };
    });
  };

  const handleEnsureInvite = async (classroom) => {
    if (!classroom?.id || isVirtualClassroom(classroom)) return;

    updateInviteState(classroom.id, (state) => ({ ...state, ensuring: true, error: '' }));

    try {
      const ensured = await ensureClassroomInvite(classroom.id, currentUser?.uid || classroom.creatorId || null);
      if (ensured) {
        setClassrooms((prev) => prev.map((item) => (
          item.id === classroom.id
            ? {
                ...item,
                inviteToken: ensured.token ?? null,
                inviteUrl: ensured.inviteUrl ?? null,
                inviteExpiresAt: ensured.expiresAt ?? null
              }
            : item
        )));
        updateInviteState(classroom.id, (state) => ({ ...state, ensuring: false }));
      } else {
        updateInviteState(classroom.id, (state) => ({ ...state, ensuring: false, error: 'Unable to generate invite link.' }));
      }
    } catch (inviteError) {
      console.error('Error ensuring classroom invite token:', inviteError);
      updateInviteState(classroom.id, (state) => ({ ...state, ensuring: false, error: 'Failed to generate invite link. Please try again.' }));
    }
  };

  const handleRotateInvite = async (classroom) => {
    if (!classroom?.id || isVirtualClassroom(classroom)) return;

    updateInviteState(classroom.id, (state) => ({ ...state, rotating: true, error: '' }));

    try {
      const rotated = await rotateClassroomInviteToken(classroom.id, currentUser?.uid || null);
      if (rotated) {
        setClassrooms((prev) => prev.map((item) => (
          item.id === classroom.id
            ? {
                ...item,
                inviteToken: rotated.token ?? null,
                inviteUrl: rotated.inviteUrl ?? null,
                inviteExpiresAt: rotated.expiresAt ?? null
              }
            : item
        )));
        updateInviteState(classroom.id, (state) => ({ ...state, rotating: false }));
      } else {
        updateInviteState(classroom.id, (state) => ({ ...state, rotating: false, error: 'Unable to refresh invite link.' }));
      }
    } catch (rotateError) {
      console.error('Error rotating classroom invite token:', rotateError);
      updateInviteState(classroom.id, (state) => ({ ...state, rotating: false, error: 'Failed to refresh invite link. Please try again.' }));
    }
  };

  const handleCopyInvite = async (classroom) => {
    if (isVirtualClassroom(classroom)) {
      return;
    }

    if (!classroom?.id || !classroom?.inviteUrl) {
      updateInviteState(classroom?.id ?? 'unknown', (state) => ({ ...state, error: 'Invite link is not available yet.' }));
      return;
    }

    const copied = await copyToClipboard(classroom.inviteUrl);
    if (copied) {
      updateInviteState(classroom.id, (state) => ({ ...state, copySuccess: true }));
      setTimeout(() => {
        setInviteActions((prev) => {
          if (!prev[classroom.id]) return prev;
          return {
            ...prev,
            [classroom.id]: {
              ...prev[classroom.id],
              copySuccess: false
            }
          };
        });
      }, 2500);
    } else {
      updateInviteState(classroom.id, (state) => ({ ...state, error: 'Could not copy invite link. Please copy manually.' }));
    }
  };

  const renderInviteExpiry = (timestamp) => {
    if (!timestamp) return 'No expiration set';

    try {
      const date = typeof timestamp.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
      return `Expires ${date.toLocaleString()}`;
    } catch (formatError) {
      console.warn('Failed to format invite expiration timestamp:', formatError);
      return 'Expiration unknown';
    }
  };

  // Helper to get category names from IDs
  const getCategoryNames = (ids) => {
    if (!ids || ids.length === 0) return 'None';
    return ids.map(id => allCategories.find(cat => cat.id === id)?.name || id).join(', ');
  };


  return (
    <div className="manage-classrooms-container">
      <h2>Manage Classrooms</h2>

      {error && <p className="error-message">{error}</p>}

      <form onSubmit={handleSubmit} className="classroom-form">
        <h3>{isEditing ? 'Edit Classroom' : 'Add New Classroom'}</h3>
        <input
          type="text"
          name="name"
          placeholder="Classroom Name (e.g., Grade 5 Math)"
          value={currentClassroom.name}
          onChange={handleInputChange}
          required
        />
        <textarea
          name="description"
          placeholder="Classroom Description (Optional)"
          value={currentClassroom.description}
          onChange={handleInputChange}
        />
         <div>
            <label htmlFor="categoryIds">Link to Categories (Hold Ctrl/Cmd to select multiple):</label>
            <select
                id="categoryIds"
                name="categoryIds"
                multiple={true}
                value={currentClassroom.categoryIds}
                onChange={handleCategoryChange}
                size="5"
            >
                {allCategories.length === 0 && <option disabled>Loading categories...</option>}
                {allCategories.map(category => (
                    <option key={category.id} value={category.id}>
                        {category.name}
                    </option>
                ))}
            </select>
        </div>

        {/* --- TODO: Add UI for managing teacherIds --- */}
        {/* Example Placeholder: */}
        {/* {isEditing && (
            <div>
                <label>Assigned Teachers (Hold Ctrl/Cmd to select multiple):</label>
                <select multiple value={currentClassroom.teacherIds} onChange={handleTeacherChange}>
                    {allTeachers.map(teacher => <option key={teacher.id} value={teacher.id}>{teacher.name}</option>)}
                </select>
            </div>
        )} */}
        {/* --- End Placeholder --- */}

        {/* Optional: Add isDefault checkbox */}
        {/* <div>
             <label>
                 <input
                    type="checkbox"
                    name="isDefault"
                    checked={currentClassroom.isDefault || false}
                    onChange={e => setCurrentClassroom({...currentClassroom, isDefault: e.target.checked})}
                 />
                 Set as Default Classroom
             </label>
        </div> */}


        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : (isEditing ? 'Update Classroom' : 'Add Classroom')}
        </button>
        {isEditing && <button type="button" onClick={resetForm} disabled={isLoading}>Cancel Edit</button>}
      </form>

      <div className="classroom-list">
        <h3>Your Classrooms</h3>
        {isLoading && classrooms.length === 0 && <p>Loading classrooms...</p>}
        {!isLoading && classrooms.length === 0 && <p>You haven't created or been assigned to any classrooms yet.</p>}
        <ul>
          {classrooms.map((classroom) => (
            <li key={classroom.id}>
              <div>
                <strong>
                  <Link
                    to={`/manage/classrooms/${classroom.id}`}
                    className="classroom-title-link"
                  >
                    {classroom.name}
                  </Link>
                </strong>
                {isVirtualClassroom(classroom) && ' (Visão administrativa)'}
                {classroom.creatorId === currentUser?.uid && " (Creator)"}
                {classroom.description && <p>{classroom.description}</p>}
                <small>
                  {isVirtualClassroom(classroom)
                    ? 'Categories: All classrooms'
                    : `Categories: ${getCategoryNames(classroom.categoryIds)}`}
                </small><br/>
                {!isVirtualClassroom(classroom) && (
                  <div className="invite-panel">
                    <label>Invite link</label>
                    {classroom.inviteUrl ? (
                      <div className="invite-actions">
                        <input
                          type="text"
                          readOnly
                          value={classroom.inviteUrl}
                          onFocus={(event) => event.target.select()}
                        />
                        <button type="button" onClick={() => handleCopyInvite(classroom)}>
                          Copy link
                        </button>
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => handleRotateInvite(classroom)}
                          disabled={inviteActions[classroom.id]?.rotating}
                          title="Generate a new invite link"
                        >
                          {inviteActions[classroom.id]?.rotating ? 'Refreshing…' : 'Refresh link'}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="generate-invite"
                        onClick={() => handleEnsureInvite(classroom)}
                        disabled={inviteActions[classroom.id]?.ensuring}
                      >
                        {inviteActions[classroom.id]?.ensuring ? 'Generating…' : 'Generate invite link'}
                      </button>
                    )}
                    <div className="invite-feedback">
                      {classroom.inviteUrl && (
                        <small className="invite-expiry">{renderInviteExpiry(classroom.inviteExpiresAt)}</small>
                      )}
                      {inviteActions[classroom.id]?.copySuccess && (
                        <small className="invite-success">Invite link copied to clipboard!</small>
                      )}
                      {inviteActions[classroom.id]?.error && (
                        <small className="invite-error">{inviteActions[classroom.id].error}</small>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="classroom-actions">
                {!isVirtualClassroom(classroom) && (
                  <>
                    <button
                        onClick={() => handleEdit(classroom)}
                        disabled={isLoading || !currentUser || !classroom.teacherIds?.includes(currentUser.uid)}
                        title={!currentUser || !classroom.teacherIds?.includes(currentUser.uid) ? "You don't have permission to edit" : "Edit Classroom"}
                    >
                        Edit
                    </button>
                    <button
                        onClick={() => handleDelete(classroom)}
                        disabled={isLoading || !currentUser || classroom.creatorId !== currentUser.uid}
                        title={!currentUser || classroom.creatorId !== currentUser.uid ? "Only the creator can delete" : "Delete Classroom"}
                    >
                        Delete
                    </button>
                  </>
                )}
                <Link
                  to={`/manage/classrooms/${classroom.id}`}
                  className="dashboard-link"
                >
                  Abrir dashboard
                </Link>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default ManageClassroomsPage;
